import React from 'react'
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'

import { useIntl } from 'react-intl'

import {
  Button,
  MessageBox,
  Modal,
  ModalHead,
  ModalBody,
  ModalFoot,
  RadioButtonsGroup,
  SingleLoader
} from 'mailup-web-components'

import { modalSendingSegmentationState } from '../recoil/state/modals'

import './ModalSendingSegmentation.scss'

import SegmentationPanel from './components/SegmentationPanel'
import SegmentationInfo from './components/SegmentationInfo'

const ModalSendingSegmentation = () => {
  const intl = useIntl()

  const {
    show,
    error,
    severity,
    loading,
    groups,
    excludedGroups,
    filters,
    section
  } = useRecoilValue(modalSendingSegmentationState)

  const setModalState = useSetRecoilState(modalSendingSegmentationState)

  const closeModal = useResetRecoilState(modalSendingSegmentationState)

  const handleSelectorChange = newValue => {
    setModalState(prev => ({
      ...prev,
      section: newValue
    }))
  }

  const noData =
    groups.length === 0 && excludedGroups.length === 0 && filters.length === 0

  const intlGroups = groups.map(group => ({
    ...group,
    name:
      group.id === -1
        ? intl.formatMessage({ id: 'Load.Filters.NoGroupOption' })
        : group.deleted
        ? intl.formatMessage(
            { id: 'Load.ModalSegmentation.GroupDeleted' },
            { x: group.id }
          )
        : 'ID ' + group.id + ' - ' + group.name
  }))

  const intlExcludedGroups = excludedGroups.map(group => ({
    ...group,
    name: group.deleted
      ? intl.formatMessage(
          { id: 'Load.ModalSegmentation.GroupDeleted' },
          { x: group.id }
        )
      : 'ID ' + group.id + ' - ' + group.name
  }))

  const intlFilters = filters.map(filter => ({
    ...filter,
    name: filter.deleted
      ? intl.formatMessage(
          { id: 'Load.ModalSegmentation.FilterDeleted' },
          { x: filter.id }
        )
      : 'ID ' + filter.id + ' - ' + filter.name
  }))

  const selectorItems = [
    {
      value: 'groups',
      label: (
        <>
          {intl.formatMessage({ id: 'Load.Statistics.IncludedGroups' })} (
          {intl.formatNumber(groups.length)})
        </>
      ),
      disabled: !groups.length,
      dataAttributeTest: 'modalSegmentation-includedGroups-click'
    },
    {
      value: 'excludedGroups',
      label: (
        <>
          {intl.formatMessage({ id: 'Load.Statistics.ExcludedGroups' })} (
          {intl.formatNumber(excludedGroups.length)})
        </>
      ),
      disabled: !excludedGroups.length,
      dataAttributeTest: 'modalSegmentation-excludedGroups-click'
    },
    {
      value: 'filters',
      label: (
        <>
          {intl.formatMessage({ id: 'Load.Common.AppliedFlters' })} (
          {intl.formatNumber(filters.length)})
        </>
      ),
      disabled: !filters.length,
      dataAttributeTest: 'modalSegmentation-appliedFlters-click'
    }
  ]

  return (
    <Modal
      show={show}
      showClose
      data-mu-test='modal-sending-segmentation'
      onClose={closeModal}
    >
      <ModalHead
        title={intl.formatMessage({ id: 'Load.ModalSegmentation.Title' })}
      />
      {error && <MessageBox variant={severity}>{error}</MessageBox>}

      {!error && (
        <>
          {loading && (
            <div style={{ height: '225px' }}>
              <SingleLoader height='225px' />
            </div>
          )}

          {!loading && (
            <>
              <ModalBody
                scrollable
                className='modal-segmentation__scrollable-body'
              >
                {noData && (
                  <div
                    style={{
                      lineHeight: '225px',
                      height: '225px',
                      textAlign: 'center'
                    }}
                  >
                    <i>
                      {intl.formatMessage({
                        id: 'Load.Common.RecipientSelectNoData'
                      })}
                    </i>
                  </div>
                )}
                {!noData && (
                  <>
                    <SegmentationInfo />

                    <div className='l-section'>
                      <RadioButtonsGroup
                        className='modal-segmentation__selector'
                        value={section}
                        items={selectorItems}
                        onChange={handleSelectorChange}
                      />
                    </div>

                    <div className='l-section'>
                      {section === 'groups' && (
                        <SegmentationPanel
                          title={intl.formatMessage({
                            id: 'Load.Statistics.IncludedGroups'
                          })}
                          items={intlGroups}
                        />
                      )}

                      {section === 'excludedGroups' && (
                        <SegmentationPanel
                          title={intl.formatMessage({
                            id: 'Load.Statistics.ExcludedGroups'
                          })}
                          items={intlExcludedGroups}
                        />
                      )}

                      {section === 'filters' && (
                        <SegmentationPanel
                          title={intl.formatMessage({
                            id: 'Load.Common.AppliedFlters'
                          })}
                          items={intlFilters}
                        />
                      )}
                    </div>
                  </>
                )}
              </ModalBody>

              <ModalFoot>
                <Button
                  data-mu-test='modalSegmentation-closeBtn-click'
                  kind='secondary'
                  onClick={closeModal}
                >
                  {intl.formatMessage({ id: 'Load.Common.Close' })}
                </Button>
              </ModalFoot>
            </>
          )}
        </>
      )}
    </Modal>
  )
}

export default ModalSendingSegmentation
